import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Slider,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { ArrowBack, ArrowForward, SaveOutlined, ShoppingCartOutlined } from '@mui/icons-material';
import Page1Settings from './Page1Settings';
import Page2Settings from './Page2Settings';
import Page3Settings from './Page3Settings';
import Page4Settings from './Page4Settings';
import Page5Settings from './Page5Settings';
import Page6Settings from './Page6Settings';
import Page7Settings from './Page7Settings';
import PreviewCard from './PreviewCard';
import PaymentPopup from '../PaymentPopup';
import { useStudio } from '../../Context/StudioContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { createCardOrderApi, getCardBySlugAndCodeApi, getGiftsApi, updateCardDraftApi } from '../../api/cardApi';
import { useAuth } from '../../Context/AuthContext';
import { deleteImages, uploadImage } from '../../services/uploadService';
import { DESIGN_PRESETS } from '../../Context/DesignPresets';

const StudioLayout = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 7;
  const { formData, setFormData } = useStudio();
  const location = useLocation();
  const navigate = useNavigate();
  const { invalidateCards } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSnackbar, setSaveSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isHydrating, setIsHydrating] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editUniqueCode, setEditUniqueCode] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const isFirstMount = React.useRef(true);
  const savedFormDataRef = React.useRef(null);
  const isSyncingSavedDataRef = React.useRef(false);

  const normalizeOrderPayload = (apiResponse, fallbackData) => {
    const responseCard =
      apiResponse?.cardData ||
      apiResponse?.data ||
      apiResponse?.card ||
      apiResponse;

    return {
      ...fallbackData,
      ...(responseCard && typeof responseCard === 'object' ? responseCard : {}),
      uniqueCode:
        responseCard?.uniqueCode ||
        apiResponse?.uniqueCode ||
        fallbackData?.uniqueCode ||
        editUniqueCode ||
        null,
      slug: responseCard?.slug || apiResponse?.slug || fallbackData?.slug || null,
      packageType:
        responseCard?.packageType ||
        apiResponse?.packageType ||
        fallbackData?.packageType,
      status:
        responseCard?.status ||
        apiResponse?.status ||
        fallbackData?.status ||
        'PENDING PAYMENT',
    };
  };

  const cloneCardData = (data) => JSON.parse(JSON.stringify(data || {}));

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    if (isSyncingSavedDataRef.current) {
      isSyncingSavedDataRef.current = false;
      return;
    }
    setHasUnsavedChanges(true);
  }, [formData]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get('id');
    const slug = queryParams.get('slug');
    const design = queryParams.get('design');

    if (id && slug) {
      setIsHydrating(true);
      setIsEditMode(true);
      setEditUniqueCode(id);

      getCardBySlugAndCodeApi(slug, id)
        .then(async data => {
          if (data) {
            const hydratedData = cloneCardData(data);

            // Fetch gift reservations and merge isReserved into each gift
            // (same logic as InvitationCardRender so the editor knows which are reserved)
            if (Array.isArray(hydratedData.gifts) && hydratedData.gifts.length > 0) {
              try {
                const giftSubmissions = await getGiftsApi(id);
                const reservedNames = new Set(
                  giftSubmissions.filter(s => s.isReserved && s.giftName).map(s => s.giftName)
                );
                const reservedLinks = new Set(
                  giftSubmissions.filter(s => s.isReserved && s.giftLink).map(s => s.giftLink)
                );
                hydratedData.gifts = hydratedData.gifts.map(g => ({
                  ...g,
                  isReserved: g.isReserved
                    || (g.giftName ? reservedNames.has(g.giftName) : false)
                    || (g.giftLink ? reservedLinks.has(g.giftLink) : false),
                }));
              } catch (giftErr) {
                console.warn('Could not fetch gift reservations:', giftErr);
              }
            }

            let hasNewDesign = false;
            if (design && DESIGN_PRESETS[design]) {
              Object.assign(hydratedData, {
                designCode: design,
                ...DESIGN_PRESETS[design]
              });
              hasNewDesign = true;
            }

            savedFormDataRef.current = cloneCardData(data);
            isSyncingSavedDataRef.current = true;
            setFormData(prev => ({ ...prev, ...hydratedData }));

            if (hasNewDesign) {
              setTimeout(() => setHasUnsavedChanges(true), 100);
            } else {
              setTimeout(() => setHasUnsavedChanges(false), 100);
            }
          }
        })
        .catch(err => {
          console.error('Error fetching draft:', err);
        })
        .finally(() => {
          setIsHydrating(false);
        });

      return;
    }

    if (design && DESIGN_PRESETS[design]) {
      setFormData(prev => ({
        ...prev,
        designCode: design,
        ...DESIGN_PRESETS[design],
      }));
    }
  }, [location.search, setFormData]);


  const handleNext = async () => {
    if (currentPage === totalPages) {
      await handleSubmit();
    } else {
      setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    }
  };

  const handleBack = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };


  // Upload any pending File objects to Supabase and replace blob URLs with public URLs
  // Organized into: {uniqueCode}/gallery/, {uniqueCode}/qr/, {uniqueCode}/gift/
  const uploadPendingImages = async (data, uniqueCode) => {
    const result = { ...data };
    const cardFolder = uniqueCode || `draft-${Date.now()}`;

    // Slider images → {cardFolder}/gallery/
    for (const key of ['sliderImage1', 'sliderImage2', 'sliderImage3']) {
      const fileKey = `${key}File`;
      if (result[fileKey] instanceof File) {
        const upload = await uploadImage(result[fileKey], `${cardFolder}/gallery`);
        if (!upload.success) {
          throw new Error(upload.message || `Failed to upload ${key}`);
        }
        result[key] = upload.publicUrl;
      }
      delete result[fileKey];
    }

    // Bank QR image → {cardFolder}/qr/
    if (result.bankQRImageFile instanceof File) {
      const upload = await uploadImage(result.bankQRImageFile, `${cardFolder}/qr`);
      if (!upload.success) {
        throw new Error(upload.message || 'Failed to upload bank QR image');
      }
      result.bankQRImage = upload.publicUrl;
    }
    delete result.bankQRImageFile;

    // Gift images → {cardFolder}/gift/
    // Form already uses giftName/giftLink/giftImage (DB format), just upload imageFile if present
    if (Array.isArray(result.gifts)) {
      result.gifts = await Promise.all(
        result.gifts.map(async (gift) => {
          const { imageFile, ...rest } = gift;

          if (imageFile instanceof File) {
            const upload = await uploadImage(imageFile, `${cardFolder}/gift`);
            if (!upload.success) {
              throw new Error(upload.message || 'Failed to upload gift image');
            }
            rest.giftImage = upload.publicUrl;
          }

          return rest;
        })
      );
    }

    return result;
  };

  const getSavedImageUrls = (data = {}) => {
    const urls = [
      data.sliderImage1,
      data.sliderImage2,
      data.sliderImage3,
      data.bankQRImage,
    ];

    if (Array.isArray(data.gifts)) {
      data.gifts.forEach((gift) => urls.push(gift?.giftImage));
    }

    return urls.filter((url) => typeof url === 'string' && url.trim() && !url.startsWith('blob:'));
  };

  const cleanupReplacedImages = async (previousData, nextData) => {
    if (!previousData) return;

    const nextUrls = new Set(getSavedImageUrls(nextData));
    const removedUrls = getSavedImageUrls(previousData).filter((url) => !nextUrls.has(url));

    if (removedUrls.length === 0) return;

    const result = await deleteImages(removedUrls);
    if (!result.success) {
      console.error('Failed to delete replaced Supabase images:', result.message);
    }
  };

  const syncSavedFormData = (savedData) => {
    const clonedSavedData = cloneCardData(savedData);
    savedFormDataRef.current = cloneCardData(clonedSavedData);
    isSyncingSavedDataRef.current = true;
    setFormData(clonedSavedData);
    setHasUnsavedChanges(false);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let updatedFormData = { ...formData, isDraft: false };

      // Upload pending images → organized into {uniqueCode}/gallery, /qr, /gift
      updatedFormData = await uploadPendingImages(updatedFormData, isEditMode ? editUniqueCode : null);

      let response;
      if (isEditMode && editUniqueCode) {
        response = await updateCardDraftApi(editUniqueCode, updatedFormData);
        await cleanupReplacedImages(savedFormDataRef.current, updatedFormData);
        invalidateCards(); // card updated — refresh cache
      } else {
        response = await createCardOrderApi(updatedFormData);
        invalidateCards(); // new card created — refresh cache
      }

      const savedOrder = normalizeOrderPayload(response, updatedFormData);
      syncSavedFormData(savedOrder);
      setSubmittedOrder(savedOrder);
      setIsPaymentDialogOpen(true);
      setSaveSnackbar({ open: true, message: 'Pesanan berjaya dicipta!', severity: 'success' });
    } catch (error) {
      console.error('Error creating order:', error);
      setSaveSnackbar({ open: true, message: 'Gagal cipta pesanan. Cuba lagi.', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleSaveDraft = async () => {
    if (!validateRequiredFields()) return;
    setIsSaving(true);
    try {
      let updatedFormData = { ...formData, isDraft: true };

      // Upload pending images → organized into {uniqueCode}/gallery, /qr, /gift
      updatedFormData = await uploadPendingImages(updatedFormData, isEditMode ? editUniqueCode : null);

      let response;
      if (isEditMode && editUniqueCode) {
        response = await updateCardDraftApi(editUniqueCode, updatedFormData);
        await cleanupReplacedImages(savedFormDataRef.current, updatedFormData);
        invalidateCards(); // card updated — refresh cache
        if (formData.status === 'ACTIVE') {
          setTimeout(() => navigate('/profile?tab=orders'), 1500);
        } else {
          setTimeout(() => navigate('/profile?tab=cart'), 1500);
        }
      } else {
        response = await createCardOrderApi(updatedFormData);
        invalidateCards(); // new draft created — refresh cache
        setTimeout(() => navigate('/profile?tab=cart'), 1500);
      }

      syncSavedFormData(normalizeOrderPayload(response, updatedFormData));
      setSaveSnackbar({ open: true, message: 'Draf berjaya disimpan!', severity: 'success' });

    } catch (error) {
      console.error('Error saving draft:', error);
      setSaveSnackbar({ open: true, message: 'Gagal simpan draf. Cuba lagi.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateOrderClick = () => {
    if (!validateRequiredFields()) return;
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmCreateOrder = async () => {
    setIsConfirmDialogOpen(false);
    await handleSubmit();
  };

  // Validates that Tarikh Majlis fields are filled before Save or Create Order
  // date input (type="date") stores value as YYYY-MM-DD only after the user picks a date.
  const validateRequiredFields = () => {
    if (!formData.greetingName || formData.greetingName === 'Nama & Nama') {
      setSaveSnackbar({ open: true, message: 'Sila isi nama anda (Pasangan 1) sebelum meneruskan.', severity: 'error' });
      return false;
    }
    const isValidDate = (val) => /^\d{4}-\d{2}-\d{2}$/.test(val);
    if (!isValidDate(formData.eventDate)) {
      setSaveSnackbar({ open: true, message: 'Sila pilih Tarikh Majlis sebelum meneruskan.', severity: 'error' });
      return false;
    }
    if (!formData.startTime) {
      setSaveSnackbar({ open: true, message: 'Sila isi Masa Majlis Bermula sebelum meneruskan.', severity: 'error' });
      return false;
    }
    if (!formData.endTime) {
      setSaveSnackbar({ open: true, message: 'Sila isi Masa Majlis Berakhir sebelum meneruskan.', severity: 'error' });
      return false;
    }
    return true;
  };

  const renderSettingsPage = () => {
    switch (currentPage) {
      case 1:
        return <Page1Settings />;
      case 2:
        return <Page2Settings />;
      case 3:
        return <Page3Settings />;
      case 4:
        return <Page4Settings />;
      case 5:
        return <Page5Settings />;
      case 6:
        return <Page6Settings />;
      case 7:
        return <Page7Settings />;
      default:
        return <Page1Settings />;
    }
  };

  if (isHydrating) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} sx={{ color: '#55C2C3' }} />
        <Typography variant="h6" sx={{ ml: 2, color: '#55C2C3' }}>Memuatkan Draf...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Container maxWidth="lg" disableGutters
        sx={{
          flexGrow: 1,
          mt: 3,
          mb: 3,
          p: 4,
          display: "flex"
        }}>
        <Grid container spacing={0}>
          <Grid item xs={12} md={6} >
            {renderSettingsPage()}

            {/* Navigation Buttons and Pagination */}
            <Box display="flex" flexDirection="column" alignItems="center" mt={4} width="100%">

              <Box
                display="flex"
                flexDirection={{ xs: 'column', lg: 'column' }}
                alignItems="center"
                justifyContent={{ xs: 'center', lg: 'space-between' }}
                width="100%"
                gap={{ xs: 1, md: 2 }}
              >
                {/* Pagination Controls */}
                <Box display="flex" alignItems="center" justifyContent="center">
                  <IconButton
                    onClick={handleBack}
                    disabled={currentPage === 1 || isSubmitting}
                    sx={{ visibility: currentPage === 1 ? 'hidden' : 'visible' }}
                  >
                    <ArrowBack />
                  </IconButton>

                  <Slider
                    value={currentPage}
                    max={totalPages}
                    min={1}
                    step={1}
                    disabled
                    sx={{
                      width: { xs: 150, sm: 200 },
                      mx: 2,
                      color: '#55C2C3',
                      '& .MuiSlider-thumb': {
                        backgroundColor: '#55C2C3',
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: '#55C2C3',
                      }
                    }}
                  />

                  <IconButton
                    onClick={handleNext}
                    disabled={isSubmitting || currentPage === totalPages}
                    sx={{ visibility: currentPage === totalPages ? 'hidden' : 'visible' }}
                  >
                    <ArrowForward />
                  </IconButton>
                </Box>

                <Typography variant="caption" align="center" >
                  {currentPage}/{totalPages}
                </Typography>

                {/* Action Buttons */}
                <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap" gap={2} mt={{ xs: 2, lg: 2 }}>
                  {/* Save Draft Button */}
                  <Button
                    variant="contained"
                    onClick={handleSaveDraft}
                    disabled={isSaving || isSubmitting || !hasUnsavedChanges || (currentPage === 1 && !hasUnsavedChanges)}
                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
                    sx={{
                      backgroundColor: '#55C2C3',
                      color: 'white',
                      textTransform: 'none',
                      borderRadius: '50px',
                      padding: '8px 20px',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: '#44B0B1',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save Draft'}
                  </Button>

                  {/* Create Order Button */}
                  {currentPage === totalPages && !(isEditMode && formData.status === 'ACTIVE') && (
                    <Button
                      variant="contained"
                      onClick={handleCreateOrderClick}
                      disabled={isSubmitting}
                      sx={{
                        backgroundColor: '#55C2C3',
                        color: 'white',
                        textTransform: 'none',
                        borderRadius: '50px',
                        padding: '8px 20px',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#44B0B1',
                          boxShadow: 'none',
                        },
                      }}
                      startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <ShoppingCartOutlined />}
                    >
                      {isSubmitting ? "Submitting..." : "Submit Order"}
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} sx={{ mt: 4, justifyContent: 'center', justifyItems: 'center' }}>
            <PreviewCard currentPage={currentPage} />
          </Grid>

        </Grid>

      </Container>

      {/* Save success/error snackbar */}
      <Snackbar
        open={saveSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setSaveSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSaveSnackbar((s) => ({ ...s, open: false }))}
          severity={saveSnackbar.severity}
          sx={{ width: '100%', borderRadius: '12px' }}
        >
          {saveSnackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={isConfirmDialogOpen}
        onClose={() => !isSubmitting && setIsConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Confirm Order</DialogTitle>
        <DialogContent>
          <Typography>
            Are you confirm to proceed with order?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setIsConfirmDialogOpen(false)}
            disabled={isSubmitting}
            sx={{ textTransform: 'none', color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCreateOrder}
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              backgroundColor: '#55C2C3',
              '&:hover': {
                backgroundColor: '#44B0B1',
              },
            }}
          >
            {isSubmitting ? 'Creating...' : 'Yes, Proceed'}
          </Button>
        </DialogActions>
      </Dialog>

      {isPaymentDialogOpen && submittedOrder && (
        <PaymentPopup
          card={submittedOrder}
          onClose={() => {
            setIsPaymentDialogOpen(false);
            navigate('/profile?tab=orders');
          }}
        />
      )}
    </>
  );
};

export default StudioLayout;
